/**
 * V&V Recommendation schema — table columns for the Step 8 V&V tab.
 *
 * The backend stores recommendations as
 *   { recommendation: { vv_id, requirement_id, requirement_title,
 *                       cal_level, recommended_methods[],
 *                       independence_level, verification_rigor,
 *                       validation_focus, success_criteria,
 *                       iso_reference, estimated_effort },
 *     rationale, alternative_methods[] }
 *
 * Components flatten the inner `recommendation` object onto the row
 * before passing it to this schema, so column keys read straight off
 * the row (e.g. `row.vv_id`, not `row.recommendation.vv_id`).
 */
import React from 'react';
import LevelBadge from '../../../../components/ui/LevelBadge';
import CategoryPill from '../../../../components/ui/CategoryPill';
import { getCALColor, getSeverityColor } from '../../../../utils/severityStyle';

export interface VVRecord {
  vv_id: string;
  requirement_id?: string;
  requirement_title?: string;
  cal_level?: string;
  recommended_methods?: string[];
  independence_level?: string;
  verification_rigor?: string;
  validation_focus?: string;
  success_criteria?: string;
  iso_reference?: string;
  estimated_effort?: string;
  rationale?: string;
  alternative_methods?: string[];
  [key: string]: unknown;
}

export const CAL_LEVELS = [
  { value: 'CAL1', label: 'CAL1' },
  { value: 'CAL2', label: 'CAL2' },
  { value: 'CAL3', label: 'CAL3' },
  { value: 'CAL4', label: 'CAL4' },
];

export const EFFORT_LEVELS = [
  { value: 'Low', label: 'Low' },
  { value: 'Medium', label: 'Medium' },
  { value: 'High', label: 'High' },
];

// Low / Medium / High effort map 1:1 onto the shared `athena` severity
// palette (Low→green, Medium→amber, High→red) — use it instead of a local
// hex map so the swatch matches the impact/CAL badges and stays token-driven.

const joinList = (v: unknown): string =>
  Array.isArray(v) ? v.join(', ') : v == null ? '' : String(v);

const renderCal = (row: VVRecord) =>
  row.cal_level ? (
    <LevelBadge
      color={getCALColor(row.cal_level)}
      label={String(row.cal_level)}
      size="sm"
    />
  ) : (
    ''
  );

const renderEffort = (row: VVRecord) =>
  row.estimated_effort ? (
    <LevelBadge color={getSeverityColor(row.estimated_effort, { scheme: 'athena' })} label={String(row.estimated_effort)} size="sm" />
  ) : (
    ''
  );

const renderRigor = (row: VVRecord) =>
  row.verification_rigor ? <CategoryPill label={String(row.verification_rigor)} size="sm" /> : '';

export const VV_COLUMNS_READ = [
  { key: 'vv_id', label: 'V&V ID', sortable: true, width: 80 },
  { key: 'requirement_id', label: 'Req ID', sortable: true, width: 90 },
  { key: 'requirement_title', label: 'Requirement', sortable: true, width: 210 },
  { key: 'cal_level', label: 'CAL', sortable: true, width: 80, render: renderCal },
  { key: 'verification_rigor', label: 'Rigor', sortable: true, width: 120, render: renderRigor },
  {
    key: 'recommended_methods',
    label: 'Recommended Methods',
    sortable: false,
    width: 320,
    render: (row: VVRecord) => joinList(row.recommended_methods),
  },
  { key: 'validation_focus', label: 'Validation Focus', sortable: false, width: 280 },
  { key: 'success_criteria', label: 'Success Criteria', sortable: false, width: 260 },
  { key: 'independence_level', label: 'Independence', sortable: true, width: 220 },
  { key: 'estimated_effort', label: 'Effort', sortable: true, width: 90, render: renderEffort },
  { key: 'iso_reference', label: 'ISO Ref', sortable: true, width: 110 },
  { key: 'rationale', label: 'Rationale', sortable: false, width: 260 },
];
